// Admin counterpart of web/src/CalibrateCylindersModal.tsx.
//
// Same UX (per-slot rows with spectrum thumb + energy + phase + a
// dropdown to assign physical cyl number), but uses admin endpoints:
//   GET  /api/admin/recordings/:id/analysis      (most-recent clip's stats)
//   PUT  /api/admin/vehicles/:id/persistent-cyls (save mapping)
// The recording list comes from the per-vehicle endpoint already
// fetched by VehicleView in AdminBrowse.

import { useEffect, useMemo, useState } from 'react';
import {
  api, parseCylAssignments,
  AdminVehicleRecordingsResponse, AnalysisResult,
  CylinderHealthResult, PerCylinderMetrics,
} from './api';

interface AdminVehicleForCalibration {
  id: number;
  name: string;
  num_cylinders: number;
  persistent_cyl_assignments?: string | null;
}

interface Props {
  vehicle: AdminVehicleForCalibration;
  onClose: () => void;
  onSaved: (assignmentsJson: string | null) => void;
}

export function CalibrateCylindersModal({ vehicle, onClose, onSaved }: Props) {
  const N = vehicle.num_cylinders;
  const initial = useMemo(() => parseCylAssignments(vehicle.persistent_cyl_assignments ?? null), [vehicle]);
  const [assignments, setAssignments] = useState<Record<number, number | ''>>(() => {
    const out: Record<number, number | ''> = {};
    for (let s = 0; s < N; s += 1) out[s] = initial[s] ?? '';
    return out;
  });
  const [latestRecording, setLatestRecording] = useState<{
    id: number;
    cylinders: CylinderHealthResult;
    rotation: number;
    confidence: number;
    template_count: number;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pull the most-recent analyzed recording for this vehicle that has
  // cycle-fingerprint data. NOT just the newest "done" — older
  // recordings may have been re-analyzed under the current pipeline
  // (so they have fingerprints) while the newest may pre-date the
  // pipeline and lack them. We use the schema-status badge ('current')
  // to filter; falls back to "none found" if nothing has been
  // re-analyzed under the current pipeline yet.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get<AdminVehicleRecordingsResponse>(`/api/admin/vehicles/${vehicle.id}/recordings`)
      .then(async (res) => {
        if (cancelled) return;
        const candidate = res.recordings
          .filter((r) => r.analysis_schema === 'current')
          .sort((a, b) => b.created_at - a.created_at)[0];
        if (!candidate) {
          setLatestRecording(null);
          return;
        }
        const detail = await api.get<{
          recording_id: number;
          analysis: AnalysisResult | null;
        }>(`/api/admin/recordings/${candidate.id}/analysis`);
        if (cancelled) return;
        const cyl = detail.analysis?.components?.cylinders;
        const align = cyl?.cycle_fingerprint_alignment;
        if (!cyl || !align) {
          // schema badge said current but the parsed JSON disagrees;
          // shouldn't happen but guard anyway.
          setLatestRecording(null);
          return;
        }
        setLatestRecording({
          id: candidate.id,
          cylinders: cyl,
          rotation: align.rotation,
          confidence: align.confidence,
          template_count: align.template_count,
        });
      })
      .catch((e) => { if (!cancelled) setError((e as Error).message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [vehicle.id]);

  // For each template slot s, find the per-slot row from the most
  // recent recording: it lives at recording-frame slot
  // (s + rotation) mod N.
  const slotsView = useMemo(() => {
    const rows: Array<{
      slot: number;
      letter: string;
      perCyl: PerCylinderMetrics | null;
    }> = [];
    for (let s = 0; s < N; s += 1) {
      let perCyl: PerCylinderMetrics | null = null;
      if (latestRecording && N > 0) {
        const recIdx = ((s + latestRecording.rotation) % N + N) % N;
        perCyl = latestRecording.cylinders.cylinders.find(
          (c) => c.slot_index === recIdx,
        ) ?? null;
      }
      rows.push({ slot: s, letter: String.fromCharCode(65 + s), perCyl });
    }
    return rows;
  }, [N, latestRecording]);

  const duplicates = useMemo(() => {
    const seen = new Map<number, number[]>();
    for (const [slotKey, val] of Object.entries(assignments)) {
      if (val === '' || val == null) continue;
      const num = Number(val);
      const slot = Number(slotKey);
      const arr = seen.get(num) ?? [];
      arr.push(slot);
      seen.set(num, arr);
    }
    const dups = new Set<number>();
    for (const [, slots] of seen) {
      if (slots.length > 1) for (const s of slots) dups.add(s);
    }
    return dups;
  }, [assignments]);

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const body: Record<string, number> = {};
      for (const [slot, cyl] of Object.entries(assignments)) {
        if (cyl !== '' && cyl != null) body[slot] = Number(cyl);
      }
      const updated = await api.put<{ id: number; persistent_cyl_assignments: string | null }>(
        `/api/admin/vehicles/${vehicle.id}/persistent-cyls`,
        { assignments: Object.keys(body).length === 0 ? null : body },
      );
      onSaved(updated.persistent_cyl_assignments);
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  function reset() {
    const next: Record<number, number | ''> = {};
    for (let s = 0; s < N; s += 1) next[s] = '';
    setAssignments(next);
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal"
        style={{ maxWidth: 720, width: '95%', maxHeight: '92vh', overflow: 'auto' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h3 style={{ margin: 0 }}>Cylinder calibration — {vehicle.name}</h3>
          <button className="modal-close" onClick={onClose} aria-label="close">×</button>
        </div>

        <div className="muted" style={{ fontSize: '0.85rem', marginBottom: 8 }}>
          Each row is a "persistent" cylinder (P-A, P-B…) the app has
          identified as the same physical cylinder across recordings.
          Use the dropdown to map each one to the engine cylinder number
          you actually want it labeled as. Partial maps are fine.
          {N > 0 && latestRecording && (
            <>
              {' '}Stats from clip <strong style={{ color: 'var(--fg)' }}>#{latestRecording.id}</strong>;
              rotation r={latestRecording.rotation},
              alignment confidence {(latestRecording.confidence * 100).toFixed(0)}%,
              template folds {latestRecording.template_count} clip{latestRecording.template_count === 1 ? '' : 's'}.
            </>
          )}
        </div>

        {loading && <div className="muted">loading latest recording…</div>}
        {!loading && !latestRecording && (
          <div className="muted" style={{ padding: '0.5rem 0' }}>
            No analyzed recording with cycle-fingerprint data yet for this
            vehicle. Run & save analysis on at least one of its recordings
            to bootstrap the alignment template; then re-open this modal.
          </div>
        )}

        <table style={{ width: '100%', fontSize: '0.85rem', marginTop: 6 }}>
          <thead>
            <tr style={{ color: 'var(--muted)', borderBottom: '1px solid var(--border)' }}>
              <th style={{ textAlign: 'left' }}>Persistent</th>
              <th style={{ textAlign: 'left' }}>Spectrum</th>
              <th style={{ textAlign: 'right' }}>Energy %</th>
              <th style={{ textAlign: 'right' }}>Phase</th>
              <th style={{ textAlign: 'left', paddingLeft: 16 }}>→ Physical cyl</th>
            </tr>
          </thead>
          <tbody>
            {slotsView.map(({ slot, letter, perCyl }) => {
              const dup = duplicates.has(slot);
              return (
                <tr key={slot} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '4px 0' }}>
                    <strong>P-{letter}</strong>
                    {perCyl?.health && (
                      <span style={{
                        marginLeft: 6, fontSize: '0.7rem',
                        color: perCyl.health === 'red' ? 'var(--danger)'
                             : perCyl.health === 'yellow' ? '#f59e0b'
                             : 'var(--ok)',
                      }}>● {perCyl.health}</span>
                    )}
                  </td>
                  <td style={{ padding: '4px 0' }}>
                    {perCyl ? <ThumbBar thumb={perCyl.spectrum_thumb} /> : <span className="muted">—</span>}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {perCyl != null
                      ? `${perCyl.energy_pct_of_cohort.toFixed(0)}%`
                      : '—'}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {perCyl != null
                      ? `${perCyl.phase_offset_deg.toFixed(1)}°`
                      : '—'}
                  </td>
                  <td style={{ paddingLeft: 16 }}>
                    <select
                      value={assignments[slot] ?? ''}
                      onChange={(e) => {
                        const v = e.target.value;
                        setAssignments((prev) => ({
                          ...prev,
                          [slot]: v === '' ? '' : Number(v),
                        }));
                      }}
                      disabled={busy}
                      style={{
                        minWidth: 160,
                        borderColor: dup ? 'var(--danger)' : undefined,
                      }}
                    >
                      <option value="">— unassigned —</option>
                      {Array.from({ length: N }, (_, i) => i + 1).map((cyl) => (
                        <option key={cyl} value={cyl}>Cyl {cyl}</option>
                      ))}
                    </select>
                    {dup && (
                      <div style={{ color: 'var(--danger)', fontSize: '0.7rem' }}>
                        duplicate
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {error && <div className="error" style={{ marginTop: 8 }}>{error}</div>}

        <div className="row" style={{ marginTop: 12, justifyContent: 'space-between' }}>
          <button className="secondary" onClick={reset} disabled={busy}>Reset all</button>
          <div className="row" style={{ gap: 6 }}>
            <button className="secondary" onClick={onClose} disabled={busy}>Cancel</button>
            <button
              onClick={save}
              disabled={busy || duplicates.size > 0}
              title={duplicates.size > 0 ? 'Resolve duplicate assignments first' : ''}
            >Save</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// 32-bin spectrum thumbnail (small SVG bar chart) — same as web copy.
function ThumbBar({ thumb }: { thumb: number[] }) {
  const W = 110;
  const H = 22;
  if (!thumb || thumb.length === 0) return <span className="muted">—</span>;
  const max = Math.max(...thumb, 1e-12);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none"
         style={{ width: W, height: H, display: 'block' }}>
      {thumb.map((v, i) => {
        const x = (i / thumb.length) * W;
        const bw = W / thumb.length;
        const h = (v / max) * H;
        return (
          <rect
            key={i}
            x={x} y={H - h}
            width={Math.max(0.5, bw - 0.4)}
            height={h}
            fill="var(--accent)"
            opacity={0.85}
          />
        );
      })}
    </svg>
  );
}
